'use client';

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommerceSource, useCart, useBlockPart, tx, PortalLink } from '@akademi/site-builder-runtime';
import type { CommerceSource } from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import { hasSavedListV2Source, type SavedListV2Source, type V2ListSummary } from './SavedListDetail';

const { sectionInner } = sharedStyles;
type Localized = string | Record<string, string>;
type SavedListLine = { productId: number; quantity: string };
type SavedList = { id: number; name: string; lines: SavedListLine[] };
type SavedListsSource = {
  listSavedLists(): Promise<SavedList[]>;
  renameSavedList(id: number, name: string): Promise<SavedList>;
  removeSavedListLine(id: number, productId: number): Promise<SavedList>;
};

export interface SavedListsViewProps {
  title?: Localized;
  emptyMessage?: Localized;
  addAllLabel?: string;
  enableV2?: boolean;
  source?: SavedListV2Source;
  scopeKey?: string;
  onOpen?: (id: string) => void;
  hrefForItem?: (id: string) => string;
  initialQuery?: string;
  initialPage?: number;
  hrefForPage?: (query: string, page: number) => string;
}

/** Renders saved lists through the host CommerceSource and the shared cart state. */
function LegacySavedListsView({ title, emptyMessage, addAllLabel = 'Add all to basket' }: SavedListsViewProps) {
  const commerce = useCommerceSource() as (CommerceSource & Partial<SavedListsSource>) | null;
  const cart = useCart();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const [lists, setLists] = useState<SavedList[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listSavedLists = commerce?.listSavedLists;
  const renameSavedList = commerce?.renameSavedList;
  const removeSavedListLine = commerce?.removeSavedListLine;
  const load = useCallback(async () => {
    if (!listSavedLists) return;
    setBusy(true); setError(null);
    try { setLists(await listSavedLists()); } catch { setError(t('commerce.savedLists.loadError')); } finally { setBusy(false); }
  }, [listSavedLists, t]);
  useEffect(() => { void load(); }, [load]);

  const rename = async (list: SavedList) => {
    const name = window.prompt('List name', list.name)?.trim();
    if (!name || name === list.name || !renameSavedList) return;
    setBusy(true); setError(null);
    try { const updated = await renameSavedList(list.id, name); setLists((current) => current.map((item) => item.id === updated.id ? updated : item)); }
    catch { setError('Could not rename the list.'); } finally { setBusy(false); }
  };

  const removeLine = async (list: SavedList, productId: number) => {
    if (!removeSavedListLine) return;
    setBusy(true); setError(null);
    try { const updated = await removeSavedListLine(list.id, productId); setLists((current) => current.map((item) => item.id === updated.id ? updated : item)); }
    catch { setError('Could not remove that line.'); } finally { setBusy(false); }
  };

  if (!listSavedLists || !renameSavedList || !removeSavedListLine) {
    return <BlockRoot as="section"><div style={sectionInner}><AdapterPlaceholder blockType="SavedListsView" adapterName="CommerceSource" reason={t('commerce.savedLists.noAdapter')} /></div></BlockRoot>;
  }

  async function addAll(list: SavedList) {
    if (!cart?.add) return;
    setBusy(true); setError(null);
    try { for (const line of list.lines) { if (!(await cart.add({ productId: line.productId, quantity: line.quantity }))) throw new Error('basket update failed'); } }
    catch { setError('Could not add all lines to the basket.'); } finally { setBusy(false); }
  }

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 960, padding: 'clamp(32px, 6vw, 72px) 24px' }}>
        <h1 {...part('title')} style={titleStyle}>{tx(title) || 'Saved lists'}</h1>
        {error && <p role="alert" style={errorStyle}>{error}</p>}
        {lists.length === 0 ? <p {...part('empty')} style={emptyStyle}>{tx(emptyMessage) || 'You have no saved lists.'}</p> :
          <div style={listStyle}>{lists.map((list) => (
            <article key={list.id} style={cardStyle}>
              <div style={headingStyle}><h2 style={listTitleStyle}>{list.name}</h2><button type="button" onClick={() => void rename(list)} disabled={busy} style={linkButtonStyle}>Rename</button></div>
              <ul style={lineListStyle}>{list.lines.map((line) => <li key={line.productId} style={lineStyle}><span>Product {line.productId} · {line.quantity}</span><button type="button" onClick={() => void removeLine(list, line.productId)} disabled={busy} style={linkButtonStyle}>Remove</button></li>)}</ul>
              <button type="button" onClick={() => void addAll(list)} disabled={busy || !cart?.add || list.lines.length === 0} style={primaryButtonStyle}>{addAllLabel}</button>
            </article>
          ))}</div>}
      </div>
    </BlockRoot>
  );
}

export function SavedListsView(props: SavedListsViewProps) {
  return props.enableV2 ? <SavedListsDirectory {...props} /> : <LegacySavedListsView {...props} />;
}

function SavedListsDirectory({ source: supplied, scopeKey, onOpen, hrefForItem, initialQuery = '', initialPage = 0, hrefForPage }: SavedListsViewProps) {
  const commerce = useCommerceSource();
  const source = supplied ?? (hasSavedListV2Source(commerce) ? commerce.buyingLists : undefined);
  const accountScope = scopeKey ?? source?.getScopeKey?.();
  const { t } = useTranslation('site-builder-blocks');
  const tr = (key: string, values?: Record<string, string | number>) => t(`commerce.savedListDirectory.${key}`, values);
  const [lists, setLists] = useState<V2ListSummary[]>([]);
  const [query, setQuery] = useState(initialQuery);
  const [page, setPage] = useState(initialPage);
  const [total, setTotal] = useState(0);
  const [name, setName] = useState('');
  const [editId, setEditId] = useState('');
  const [editName, setEditName] = useState('');
  const [deleteId, setDeleteId] = useState('');
  const [conflictId, setConflictId] = useState('');
  const [reviewedRevision, setReviewedRevision] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const epoch = useRef(0), request = useRef(0), pending = useRef(false);
  const deleteTriggers = useRef(new Map<string, HTMLButtonElement>());
  const deleteDialog = useRef<HTMLDivElement>(null);
  const cancelDelete = (id: string) => { setDeleteId(''); deleteTriggers.current.get(id)?.focus(); };
  useEffect(() => { if (deleteId) deleteDialog.current?.querySelector('button')?.focus(); }, [deleteId]);
  const load = async (q = query, nextPage = page) => {
    if (!source || !accountScope) return;
    const current = epoch.current, sequence = ++request.current;
    setBusy(true); setMessage('');
    try { const result = await source.list({ q: q.trim() || undefined, page: nextPage, size: 50 });
      if (epoch.current !== current || request.current !== sequence) return;
      setLists(result.items); setTotal(result.total); setPage(nextPage);
    } catch { if (epoch.current === current && request.current === sequence) setMessage(tr('loadError')); }
    finally { if (epoch.current === current && request.current === sequence) setBusy(false); }
  };
  useEffect(() => {
    epoch.current++; request.current++;
    setLists([]); setQuery(initialQuery); setPage(initialPage); setTotal(0); setName(''); setEditId(''); setDeleteId(''); setConflictId(''); setReviewedRevision(''); setMessage('');
    if (source && accountScope) void load(initialQuery, initialPage);
    return () => { epoch.current++; request.current++; };
  }, [source, accountScope, initialQuery, initialPage]);
  const mutate = async (listId: string, work: (revision: string) => Promise<unknown>) => {
    if (!source || pending.current || conflictId) return;
    const list = lists.find((item) => item.id === listId);
    if (!list) return;
    const current = epoch.current; pending.current = true; setBusy(true); setMessage('');
    try { await work(list.revision); if (current !== epoch.current) return;
      setEditId(''); setDeleteId(''); setReviewedRevision(''); await load(query, page);
    } catch (cause) { if (current !== epoch.current) return;
      if (cause && typeof cause === 'object' && 'status' in cause && cause.status === 409) { setConflictId(listId); setMessage(tr('conflict')); }
      else setMessage(tr('saveError'));
    } finally { pending.current = false; if (current === epoch.current) setBusy(false); }
  };
  const review = async () => { if (!source || !conflictId) return; const current = epoch.current;
    try { const latest = await source.detail(conflictId); if (current === epoch.current) { setReviewedRevision(latest.revision); setMessage(tr('review', { name: latest.name, revision: latest.revision })); } }
    catch { if (current === epoch.current) setMessage(tr('loadError')); }
  };
  if (!source || !accountScope) return <BlockRoot as="section"><p role="status">{tr('unavailable')}</p></BlockRoot>;
  return <BlockRoot as="section"><div style={{ ...sectionInner, maxWidth: 960, padding: 'clamp(20px, 5vw, 64px) 16px' }}>
    <h1>{tr('title')}</h1>
    <form method={hrefForPage ? 'GET' : undefined} action={hrefForPage ? hrefForPage('', 0) : undefined} onSubmit={hrefForPage ? undefined : (event) => { event.preventDefault(); void load(query, 0); }}><label>{tr('search')}<input name={hrefForPage ? 'q' : undefined} value={query} maxLength={100} onChange={(event) => { request.current++; setQuery(event.target.value); }} /></label><button type="submit">{tr('searchButton')}</button></form>
    <form onSubmit={(event) => { event.preventDefault(); if (!name.trim() || name.trim().length > 200 || pending.current) { setMessage(tr('invalidName')); return; }
      const current = epoch.current; pending.current = true; setBusy(true); void source.create({ name: name.trim(), items: [] }).then(() => {
        if (current !== epoch.current) return; setName(''); void load(query, page);
      }).catch(() => { if (current === epoch.current) setMessage(tr('saveError')); }).finally(() => { pending.current = false; if (current === epoch.current) setBusy(false); });
    }}><label>{tr('newName')}<input value={name} maxLength={200} onChange={(event) => setName(event.target.value)} /></label><button type="submit" disabled={busy}>{tr('create')}</button></form>
    {message && <p role="alert">{message}</p>}
    {conflictId && <div><button type="button" onClick={() => void review()}>{tr('reviewLatest')}</button>
      {reviewedRevision && <button type="button" onClick={() => { setLists((current) => current.map((item) => item.id === conflictId ? { ...item, revision: reviewedRevision } : item)); setConflictId(''); setReviewedRevision(''); setMessage(tr('reviewAccepted')); }}>{tr('applyReviewed')}</button>}</div>}
    {lists.length === 0 && !busy && <p>{tr('empty')}</p>}
    <ul style={listStyle}>{lists.map((list) => <li key={list.id} style={cardStyle}>
      <h2>{list.name}</h2><p>{new Date(list.updatedAt).toLocaleString()} · {tr('counts', { products: list.productCount, notes: list.noteCount })} · {list.access === 'ACCOUNT_SHARED' ? tr('shared') : tr('private')}</p>
      {hrefForItem ? <PortalLink href={hrefForItem(list.id)}>{tr('open')}</PortalLink> : onOpen ? <button type="button" onClick={() => onOpen(list.id)}>{tr('open')}</button> : <PortalLink href={`/account/lists/${encodeURIComponent(list.id)}`}>{tr('open')}</PortalLink>}
      <button type="button" onClick={() => { setEditId(list.id); setEditName(list.name); }}>{tr('rename')}</button>
      {editId === list.id && <form onSubmit={(event) => { event.preventDefault(); if (!editName.trim() || editName.trim().length > 200) { setMessage(tr('invalidName')); return; }
        void mutate(list.id, (revision) => source.patch(list.id,revision,{ name: editName.trim() })); }}><label>{tr('newName')}<input value={editName} maxLength={200} onChange={(event) => setEditName(event.target.value)} /></label><button type="submit" disabled={busy || Boolean(conflictId)}>{tr('save')}</button><button type="button" onClick={() => setEditId('')}>{tr('cancel')}</button></form>}
      <button type="button" ref={(element) => { if (element) deleteTriggers.current.set(list.id, element); else deleteTriggers.current.delete(list.id); }} onClick={() => setDeleteId(list.id)}>{tr('delete')}</button>
      {deleteId === list.id && <div ref={deleteDialog} role="dialog" aria-modal="true" aria-label={tr('deleteConfirm', { name: list.name })}
        style={{ position: 'fixed', zIndex: 1000, inset: 'auto 1rem 1rem auto', maxWidth: 'min(420px, calc(100vw - 2rem))', padding: 20,
          border: '1px solid var(--color-border-default, #ccc)', borderRadius: 'var(--radius-card, 10px)',
          background: 'var(--color-surface-card, #fff)', boxShadow: 'var(--shadow-lg, 0 16px 36px rgba(0,0,0,.13))' }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') { event.stopPropagation(); cancelDelete(list.id); }
          if (event.key === 'Tab') { const buttons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
            if (event.shiftKey && document.activeElement === buttons[0]) { event.preventDefault(); buttons[buttons.length - 1]?.focus(); }
            else if (!event.shiftKey && document.activeElement === buttons[buttons.length - 1]) { event.preventDefault(); buttons[0]?.focus(); } }
        }}><p>{tr('deleteConfirm', { name: list.name })}</p>
        <button type="button" disabled={busy || Boolean(conflictId)} onClick={() => void mutate(list.id, (revision) => source.delete(list.id,revision))}>{tr('confirmDelete')}</button>
        <button type="button" onClick={() => cancelDelete(list.id)}>{tr('cancel')}</button></div>}
    </li>)}</ul>
    {total > 50 && <nav aria-label={tr('pages')} style={headingStyle}>
      {hrefForPage ? page > 0 ? <PortalLink href={hrefForPage(query, page-1)}>{tr('previous')}</PortalLink> : <span aria-disabled="true">{tr('previous')}</span> : <button type="button" disabled={page === 0 || busy} onClick={() => void load(query,page-1)}>{tr('previous')}</button>}
      <span>{tr('page', { page: page+1, pages: Math.ceil(total/50) })}</span>
      {hrefForPage ? (page+1)*50 < total ? <PortalLink href={hrefForPage(query, page+1)}>{tr('next')}</PortalLink> : <span aria-disabled="true">{tr('next')}</span> : <button type="button" disabled={(page+1)*50 >= total || busy} onClick={() => void load(query,page+1)}>{tr('next')}</button>}
    </nav>}
  </div></BlockRoot>;
}

const titleStyle: CSSProperties = { margin: '0 0 32px', fontSize: 'clamp(34px, 5vw, 56px)', lineHeight: 1.05 };
const errorStyle: CSSProperties = { margin: '0 0 20px', padding: '12px 16px', color: '#b91c1c', background: '#fef2f2', borderRadius: 8 };
const emptyStyle: CSSProperties = { margin: 0, padding: '24px 0' };
const listStyle: CSSProperties = { display: 'grid', gap: 16 };
const cardStyle: CSSProperties = { padding: 20, border: '1px solid var(--color-border-subtle, #e5e7eb)', borderRadius: 'var(--radius-card, 10px)' };
const headingStyle: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 };
const listTitleStyle: CSSProperties = { margin: 0, fontSize: 22 };
const lineListStyle: CSSProperties = { display: 'grid', gap: 8, padding: 0, margin: '16px 0', listStyle: 'none' };
const lineStyle: CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 12 };
const linkButtonStyle: CSSProperties = { border: 0, padding: 0, background: 'none', color: 'inherit', cursor: 'pointer', textDecoration: 'underline' };
const primaryButtonStyle: CSSProperties = { border: 0, borderRadius: 6, padding: '10px 14px', background: 'var(--color-primary, #111827)', color: '#fff', cursor: 'pointer' };
